import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Customers
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Leads
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  convert: (id, customerData) => api.post(`/leads/${id}/convert`, customerData),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  recordPayment: (id, data) => api.post(`/invoices/${id}/record-payment`, data),
  sendReminder: (id) => api.post(`/invoices/${id}/send-reminder`),
};

// Payments
export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Communications
export const communicationsAPI = {
  getAll: (params) => api.get('/communications', { params }),
  create: (data) => api.post('/communications', data),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Reports
export const reportsAPI = {
  getSummary: () => api.get('/reports/summary'),
  getPayments: (params) => api.get('/reports/payments', { params }),
  getExpiringContracts: (days = 30) => api.get(`/reports/expiring-contracts?days=${days}`),
};

// Contracts
export const contractsAPI = {
  getAll: (params) => api.get('/contracts', { params }),
  getOne: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  delete: (id) => api.delete(`/contracts/${id}`),
  // Public endpoints (no auth)
  getPublic: (token) => axios.get(`${API_URL}/api/contracts/public/${token}`),
  acceptPublic: (token) => axios.post(`${API_URL}/api/contracts/public/${token}/accept`),
};

// Public Invoice
export const publicInvoiceAPI = {
  getInvoice: (token) => axios.get(`${API_URL}/api/invoices/public/${token}`),
};

// Customer Portal (public)
export const portalAPI = {
  getInvoices: (phone) => api.get(`/portal/invoices/${phone}`),
};

// Admin Portal Users Management
export const adminPortalAPI = {
  getAll: () => api.get('/admin/portal-users'),
  resetPassword: (portalUserId) => api.post(`/admin/portal-users/${portalUserId}/reset-password`),
  delete: (portalUserId) => api.delete(`/admin/portal-users/${portalUserId}`),
};

export default api;
